// src/layouts/SidebarLayout.tsx
import React, { useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLayout } from '../contexts/LayoutContext';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import Footer from '../components/layout/Footer';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { setPostLoginRedirect } from '../utils/postLoginRedirect';
import { OpenIntentionPeriodProvider } from '../contexts/OpenIntentionPeriodContext';
import { CommandPaletteProvider } from '../components/layout/CommandPalette';
import SheldonWidget from '../components/SheldonWidget';
const SidebarLayout: React.FC = () => {
  const {
    user,
    loading
  } = useAuth();
  const {
    sidebarVisible,
    sidebarOpen,
    setSidebarOpen,
    setSidebarVisible
  } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle teacher-specific UI configuration
  // CRITICAL: This useEffect MUST be called before any early returns
  useEffect(() => {
    if (user?.role === 'teacher') {
      // Hide sidebar for teachers - they don't need navigation
      setSidebarVisible(false);

      // Redirect teachers from dashboard to their staff dashboard
      if (location.pathname === '/dashboard') {
        navigate('/load-letter/staff-dashboard', { replace: true });
      }
    } else if (user) {
      // Ensure sidebar is visible for non-teacher roles
      setSidebarVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, location.pathname]);

  // Show loading spinner while checking authentication
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
      </div>;
  }

  // Redirect to login if not authenticated, remembering where the user was
  // headed so we can return them there after SSO sign-in.
  if (!user) {
    setPostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return (
    <OpenIntentionPeriodProvider>
      <CommandPaletteProvider>
      <div className="min-h-screen bg-ink-50 flex flex-col">
        {/* Impersonation Banner - Always at the top */}
        <ImpersonationBanner />

        <div className="flex flex-1">
          {/* Conditionally render Sidebar based on layout context */}
          {sidebarVisible && <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Top Bar */}
            <TopBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} showSidebarToggle={sidebarVisible} />

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto">
              <div className="max-w-page mx-auto p-4 sm:p-7">
                <Outlet />
              </div>
            </main>

            {/* Footer with version info */}
            <Footer />
          </div>
        </div>

        {/* Sheldon support agent (floating widget) */}
        <SheldonWidget />
      </div>
      </CommandPaletteProvider>
    </OpenIntentionPeriodProvider>
  );
};
export default SidebarLayout;